import {
  DataTable,
  type DataTableSort,
  FilterBar,
  FilterSelect,
  formatCentsBRL,
  formatNumber,
  formatRelativeTime,
  PageLayout,
  SearchInput,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Building2Icon } from 'lucide-react'
import { useMemo } from 'react'

import { AttentionBadges } from '@/components/attention-badges'
import { ErrorState } from '@/components/error-state'
import { OrgCell } from '@/components/org-cell'
import { StatusBadge } from '@/components/status-badge'
import { useAdminOverview } from '@/hooks/use-admin-overview'
import type {
  AdminOrganizationRow,
  BillingSource,
  PlanType,
} from '@/lib/api-types'
import { attentionSignals, marginCents, usageRatio } from '@/lib/attention'
import { translateStatus } from '@/lib/labels'

const STATUS_FILTERS = [
  { value: 'paying', label: 'Pagantes' },
  { value: 'TRIALING', label: 'Em teste' },
  { value: 'PAST_DUE', label: 'Pagamento atrasado' },
  { value: 'SUSPENDED', label: 'Suspensas' },
  { value: 'CANCELED', label: 'Canceladas' },
  { value: 'INACTIVE', label: 'Inativas' },
  { value: 'none', label: 'Sem assinatura' },
] as const

const PLANS: PlanType[] = [
  'TRIAL',
  'STARTER',
  'PRO',
  'SCALE',
  'ENTERPRISE',
  'PARTNER',
  'COURTESY',
]
const SOURCES: BillingSource[] = ['STRIPE', 'LEGACY', 'COURTESY', 'TRIAL']

const SIGNAL_FILTERS = [
  { value: 'any', label: 'Qualquer sinal' },
  { value: 'past-due', label: 'Pagamento atrasado' },
  { value: 'suspended', label: 'Suspensa' },
  { value: 'no-contract', label: 'Contrato ausente' },
  { value: 'negative-margin', label: 'Margem negativa' },
  { value: 'inactive', label: 'Sem uso' },
  { value: 'trial-ending', label: 'Teste acabando' },
  { value: 'downgrade', label: 'Mudança agendada' },
  { value: 'near-limit', label: 'Perto do limite' },
  { value: 'no-plan', label: 'Cadastro sem plano' },
] as const

type StatusFilter = (typeof STATUS_FILTERS)[number]['value']
type SignalFilter = (typeof SIGNAL_FILTERS)[number]['value']

type OrganizationsSearch = {
  q?: string
  status?: StatusFilter
  plano?: PlanType
  origem?: BillingSource
  sinal?: SignalFilter
  /** `coluna.asc` / `coluna.desc` */
  ordem?: string
  pagina?: number
}

function pick<T extends string>(value: unknown, allowed: readonly T[]) {
  return allowed.includes(value as T) ? (value as T) : undefined
}

export const Route = createFileRoute('/_app/organizacoes/')({
  validateSearch: (search: Record<string, unknown>): OrganizationsSearch => ({
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    status: pick(
      search.status,
      STATUS_FILTERS.map((item) => item.value)
    ),
    plano: pick(search.plano, PLANS),
    origem: pick(search.origem, SOURCES),
    sinal: pick(
      search.sinal,
      SIGNAL_FILTERS.map((item) => item.value)
    ),
    ordem:
      typeof search.ordem === 'string' &&
      /^[a-z]+\.(asc|desc)$/.test(search.ordem)
        ? search.ordem
        : undefined,
    pagina:
      typeof search.pagina === 'number' && search.pagina > 1
        ? Math.floor(search.pagina)
        : undefined,
  }),
  component: OrganizationsPage,
})

function matchesStatus(row: AdminOrganizationRow, status: StatusFilter) {
  if (status === 'paying') return row.mrr.cents > 0
  if (status === 'none') return !row.subscription
  return row.subscription?.status === status
}

function OrganizationsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const overview = useAdminOverview()
  const usdToBrl = overview.data?.exchangeRate.rate ?? null

  const update = (patch: Partial<OrganizationsSearch>) =>
    void navigate({
      search: (previous) => ({ ...previous, pagina: undefined, ...patch }),
      replace: true,
    })

  const rows = useMemo(() => {
    const term = search.q?.trim().toLowerCase()
    return (overview.data?.organizations ?? [])
      .map((row) => ({ ...row, signals: attentionSignals(row, { usdToBrl }) }))
      .filter((row) => {
        if (search.status && !matchesStatus(row, search.status)) return false
        if (search.plano && row.subscription?.plan !== search.plano) {
          return false
        }
        if (
          search.origem &&
          row.subscription?.billingSource !== search.origem
        ) {
          return false
        }
        if (search.sinal === 'any' && row.signals.length === 0) return false
        if (
          search.sinal &&
          search.sinal !== 'any' &&
          !row.signals.some((signal) => signal.key === search.sinal)
        ) {
          return false
        }
        if (!term) return true
        return [
          row.name,
          row.slug,
          row.id,
          row.subscription?.stripeCustomerId,
          row.subscription?.stripeSubscriptionId,
        ]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(term))
      })
  }, [overview.data, search, usdToBrl])

  const [sortKey, sortDirection] = search.ordem?.split('.') ?? []
  const sort: DataTableSort | null = sortKey
    ? { key: sortKey, direction: sortDirection === 'desc' ? 'desc' : 'asc' }
    : null
  const filtered = Boolean(
    search.q || search.status || search.plano || search.origem || search.sinal
  )
  const total = overview.data?.organizations.length ?? 0

  const layout = {
    icon: Building2Icon,
    title: 'Organizações',
    description: 'Clientes, assinaturas, receita, uso e sinais de atenção',
  }

  if (overview.isError) {
    return (
      <PageLayout {...layout}>
        <ErrorState onRetry={() => void overview.refetch()} />
      </PageLayout>
    )
  }

  return (
    <PageLayout {...layout} contentClassName="space-y-4">
      <FilterBar
        active={filtered}
        onClear={() => void navigate({ search: {}, replace: true })}
        end={
          overview.data
            ? `${formatNumber(rows.length)} de ${formatNumber(total)}`
            : null
        }
      >
        <SearchInput
          className="max-w-xs"
          value={search.q ?? ''}
          onChange={(event) => update({ q: event.target.value || undefined })}
          placeholder="Nome, slug, ID ou Stripe"
        />
        <FilterSelect
          label="Status"
          value={search.status ?? 'all'}
          onChange={(value) =>
            update({ status: value === 'all' ? undefined : value })
          }
          options={STATUS_FILTERS}
        />
        <FilterSelect
          label="Plano"
          value={search.plano ?? 'all'}
          onChange={(value) =>
            update({ plano: value === 'all' ? undefined : value })
          }
          options={PLANS.map((plan) => ({
            value: plan,
            label: translateStatus(plan) ?? plan,
          }))}
          className="w-36"
        />
        <FilterSelect
          label="Cobrança"
          value={search.origem ?? 'all'}
          onChange={(value) =>
            update({ origem: value === 'all' ? undefined : value })
          }
          options={SOURCES.map((source) => ({
            value: source,
            label: translateStatus(source) ?? source,
          }))}
          className="w-40"
        />
        <FilterSelect
          label="Sinal"
          value={search.sinal ?? 'all'}
          onChange={(value) =>
            update({ sinal: value === 'all' ? undefined : value })
          }
          options={SIGNAL_FILTERS}
          className="w-48"
        />
      </FilterBar>

      <DataTable
        rows={rows}
        loading={overview.isLoading}
        getRowKey={(row) => row.id}
        empty={
          filtered
            ? 'Nenhuma organização com esses filtros.'
            : 'Nenhuma organização cadastrada.'
        }
        pageSize={25}
        page={search.pagina ?? 1}
        onPageChange={(pagina) =>
          void navigate({
            search: (previous) => ({
              ...previous,
              pagina: pagina > 1 ? pagina : undefined,
            }),
            replace: true,
          })
        }
        sort={sort}
        onSortChange={(next) =>
          update({
            ordem: next ? `${next.key}.${next.direction}` : undefined,
          })
        }
        onRowClick={(row) =>
          void navigate({
            to: '/organizacoes/$orgId',
            params: { orgId: row.id },
          })
        }
        columns={[
          {
            key: 'nome',
            header: 'Organização',
            sortValue: (row) => row.name,
            render: (row) => (
              <OrgCell
                id={row.id}
                name={row.name}
                slug={row.slug}
                logo={row.logo}
              />
            ),
          },
          {
            key: 'plano',
            header: 'Plano',
            sortValue: (row) => row.subscription?.plan,
            render: (row) =>
              row.subscription ? (
                <StatusBadge value={row.subscription.plan} tone="neutral" />
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
          },
          {
            key: 'status',
            header: 'Status',
            sortValue: (row) => row.subscription?.status,
            render: (row) => <StatusBadge value={row.subscription?.status} />,
          },
          {
            key: 'mrr',
            header: 'MRR',
            align: 'right',
            sortValue: (row) => row.mrr.cents,
            render: (row) =>
              row.mrr.cents > 0 ? (
                <div>
                  <p className="font-medium">{formatCentsBRL(row.mrr.cents)}</p>
                  {row.mrr.source === 'CATALOG' ? (
                    <p className="text-muted-foreground text-xs">estimado</p>
                  ) : null}
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
          },
          {
            key: 'uso',
            header: 'Mensagens',
            align: 'right',
            sortValue: (row) => usageRatio(row),
            render: (row) => <UsageCell row={row} />,
          },
          {
            key: 'margem',
            header: 'Margem IA (30d)',
            align: 'right',
            sortValue: (row) => marginCents(row, usdToBrl),
            render: (row) => {
              const margin = marginCents(row, usdToBrl)
              if (margin == null) {
                return <span className="text-muted-foreground">—</span>
              }
              return (
                <span
                  className={
                    margin < 0
                      ? 'text-destructive-soft-foreground font-medium'
                      : undefined
                  }
                  title="MRR menos o custo de IA dos últimos 30 dias"
                >
                  {formatCentsBRL(margin)}
                </span>
              )
            },
          },
          {
            key: 'atividade',
            header: 'Última atividade',
            sortValue: (row) => row.lastActivityAt,
            render: (row) =>
              row.lastActivityAt ? (
                <span title={row.lastActivityAt}>
                  {formatRelativeTime(row.lastActivityAt)}
                </span>
              ) : (
                <span className="text-muted-foreground">nunca</span>
              ),
          },
          {
            key: 'sinal',
            header: 'Sinal',
            sortValue: (row) => -(row.signals[0]?.priority ?? 0),
            render: (row) => (
              <div className="max-w-56">
                <AttentionBadges signals={row.signals} />
              </div>
            ),
          },
        ]}
      />
    </PageLayout>
  )
}

function UsageCell({ row }: { row: AdminOrganizationRow }) {
  const ratio = usageRatio(row)
  return (
    <div className="ml-auto w-28">
      <p>
        {formatNumber(row.usage.messages)}
        {row.usage.messagesLimit ? (
          <span className="text-muted-foreground text-xs">
            {' '}
            / {formatNumber(row.usage.messagesLimit)}
          </span>
        ) : null}
      </p>
      {ratio != null ? (
        <div className="bg-muted mt-1 h-1 overflow-hidden rounded-full">
          <div
            className={
              ratio >= 1
                ? 'bg-destructive h-full'
                : ratio >= 0.8
                  ? 'bg-warning h-full'
                  : 'bg-primary h-full'
            }
            style={{ width: `${Math.min(1, ratio) * 100}%` }}
          />
        </div>
      ) : null}
    </div>
  )
}
